import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import Brand from '../components/Brand.jsx';
import BrandFooter from '../components/BrandFooter.jsx';
import SectionCard from '../components/SectionCard.jsx';
import { markConsentStarted } from '../lib/consent-progress.js';
import { api } from '../lib/api.js';

// Where Microsoft returns the Global Administrator in the two chained
// admin-consent steps (ENTRAID-248, SEC-001 / ENTRAID-261). Public on
// purpose: the admin who approves is often a different person, in a
// different browser, with no onGuard login. Microsoft appends one of:
//   step 1 (sign-in):      ?code=<authorization code>&state=<customerId>.<random>
//   step 2 (admin consent): ?admin_consent=True&tenant=...&state=<customerId>.<random>
//   failure:               ?error=<code>&error_description=<text>&state=...
// The page hands code and state (step 1) or only the state (step 2) to the
// portal backend (POST /consent/callback). After step 1 the backend answers
// with the step 2 URL and the page goes there at once. Nothing in the query
// string is trusted: tenant and admin_consent are never read or sent.
// The redirect URI (https://<portal>/consent-complete) must be registered
// exactly on the shared IPC onGuard app; see backend lib/msgraph.mjs.
//
// Anyone can craft a link to this page, so nothing from the query string is
// shown as prose (security review wave 3, SEC-030): error_description is
// never rendered; the error code picks one of the fixed messages below and
// is shown only when it has the shape of an OAuth error code.

// state is "<customerId>.<random>"; the customerId is a slug. Anything else
// is ignored so the "return to onboarding" link can never point somewhere
// unexpected.
const STATE_RE = /^([a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?)\.[A-Za-z0-9_-]{43}$/;
// OAuth 2.0 / Microsoft identity platform error codes: lowercase and underscores.
const ERROR_CODE_RE = /^[a-z][a-z_]{0,63}$/;
const CODE_RE = /^[\x21-\x7e]{10,4096}$/;
// Microsoft Graph application permission names, e.g. AccessReview.Read.All.
const PERMISSION_NAME_RE = /^[A-Za-z][A-Za-z0-9]*(\.[A-Za-z0-9]+){0,3}$/;
const LIVE_STATUSES = new Set(['deployed', 'active']);

// Fixed explanations for the codes Microsoft returns; any other code gets
// DEFAULT_ERROR_MESSAGE.
export const CONSENT_ERROR_MESSAGES = Object.freeze({
  access_denied: 'The administrator declined, or the account used is not allowed to grant consent for the organization.',
  consent_required: 'Consent for the organization is still required.',
  interaction_required: 'Microsoft needs the administrator to sign in again and approve.',
  login_required: 'Microsoft needs the administrator to sign in again and approve.',
  invalid_request: 'Microsoft could not process the consent request. Open the link again from the onboarding page.',
  invalid_client: 'Microsoft did not recognize the onGuard application. Contact support.',
  unauthorized_client: 'The onGuard application is not allowed in this organization. Contact support.',
  invalid_resource: 'A permission onGuard asks for is not available in this organization. Contact support.',
  server_error: 'Microsoft had a temporary problem. Try the consent link again.',
  temporarily_unavailable: 'Microsoft had a temporary problem. Try the consent link again.',
});
export const DEFAULT_ERROR_MESSAGE = 'Microsoft did not record the consent.';

// Fixed explanations for the backend's outcome codes (never its free text).
export const CALLBACK_MESSAGES = Object.freeze({
  invalid: 'This consent link is invalid, already used, or expired. Open a new one from the onboarding page.',
  'tenant-taken': 'This Microsoft Entra tenant is already connected to another onGuard customer. Contact support@ipc-global.com.',
  'tenant-locked': 'This onGuard customer is already connected to a different Microsoft Entra tenant. Contact support@ipc-global.com.',
  'rate-limited': 'Too many attempts. Wait a few minutes, then open a new consent link from the onboarding page.',
  error: 'Microsoft did not confirm the sign-in. Open a new consent link from the onboarding page and try again.',
});

export function readConsentResult(params) {
  const m = STATE_RE.exec(params.get('state') || '');
  const customerId = m ? m[1] : null;
  const rawError = params.get('error');
  if (rawError) {
    const error = ERROR_CODE_RE.test(rawError) ? rawError : null;
    const message = (error && Object.prototype.hasOwnProperty.call(CONSENT_ERROR_MESSAGES, error))
      ? CONSENT_ERROR_MESSAGES[error]
      : DEFAULT_ERROR_MESSAGE;
    // Only the AADSTS number is kept from error_description (never shown).
    const aadsts = (/AADSTS(\d{5,7})/.exec(params.get('error_description') || '') || [])[1] || null;
    return { outcome: 'error', error, message, customerId, state: m ? m[0] : null, aadsts };
  }
  const code = params.get('code') || '';
  if (m && CODE_RE.test(code)) return { outcome: 'code', code, state: m[0], customerId };
  // Step 2 returned: only the state matters (the backend checks the consent).
  if (m && params.has('admin_consent')) return { outcome: 'code', code: null, state: m[0], customerId };
  return { outcome: 'unknown', customerId };
}

// Seconds before a granted consent returns to onboarding on its own (ENTRAID-260).
export const RETURN_COUNTDOWN_SECONDS = 5;

// One exchange per state, even when React mounts the page twice (StrictMode)
// or it re-renders: the backend treats the state as single use.
const exchanges = new Map();
const reported = new Set();

// The step 2 URL the backend returns must be Microsoft's v2 admin-consent
// endpoint for one tenant, returning to this portal; anything else is
// refused rather than followed.
const TENANT_PATH_RE = /^\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/v2\.0\/adminconsent$/;
export function isAdminConsentUrl(raw, origin = window.location.origin) {
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' && url.hostname === 'login.microsoftonline.com' && !url.port
      && TENANT_PATH_RE.test(url.pathname) && url.searchParams.get('redirect_uri') === `${origin}/consent-complete`;
  } catch {
    return false;
  }
}

function exchangeOnce(state, code) {
  if (!exchanges.has(state)) {
    exchanges.set(state, api.consentCallback(state, code ?? undefined)
      .then((r) => {
        if (r?.outcome === 'consent') {
          return isAdminConsentUrl(r.consentUrl)
            ? { outcome: 'redirecting', url: r.consentUrl }
            : { outcome: 'failed', message: CALLBACK_MESSAGES.error };
        }
        const outcome = r?.outcome === 'granted' ? 'granted' : r?.outcome === 'needs-approval' ? 'approval' : 'pending';
        // ENTRAID-317: a re-consent of a deployed customer refreshes permissions; the names are shown in <code>.
        if (outcome === 'granted' && r.permissionRefresh === true) {
          const granted = Array.isArray(r.granted) ? r.granted.filter((n) => typeof n === 'string' && PERMISSION_NAME_RE.test(n)) : [];
          const enabledSources = Array.isArray(r.enabledSources) ? r.enabledSources.filter((n) => typeof n === 'string' && n.length > 0) : [];
          return { outcome, permissionRefresh: true, granted, enabledSources, status: typeof r.status === 'string' ? r.status : null };
        }
        // ENTRAID-350: the consent is recorded and Microsoft is still applying it; no second consent is needed.
        return { outcome, status: typeof r?.status === 'string' ? r.status : null, settling: outcome === 'pending' && r?.settling === true };
      })
      .catch((e) => {
        const o = e?.body?.outcome;
        return { outcome: 'failed', message: CALLBACK_MESSAGES[Object.prototype.hasOwnProperty.call(CALLBACK_MESSAGES, o) ? o : 'error'] };
      }));
  }
  return exchanges.get(state);
}

// Indirection so tests can observe the navigation.
export const navigation = { assign: (url) => window.location.assign(url) };
function redirectTo(url) {
  navigation.assign(url);
}

export default function ConsentComplete() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Read once: the code is removed from the address bar right away, so it is
  // neither reused on reload nor kept in history.
  const [initial] = useState(() => readConsentResult(params));
  const [exchange, setExchange] = useState(initial.outcome === 'code' ? { outcome: 'exchanging' } : null);

  // Diagnostics (ENTRAID-261): tell the backend log which Microsoft error
  // ended the consent. Best effort, once per state; nothing is consumed.
  useEffect(() => {
    if (initial.outcome !== 'error' || !initial.state || reported.has(initial.state)) return;
    reported.add(initial.state);
    api.reportConsentError(initial.state, initial.error, initial.aadsts).catch(() => {});
  }, [initial]);

  useEffect(() => {
    if (initial.outcome !== 'code') return undefined;
    try { window.history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ }
    let live = true;
    exchangeOnce(initial.state, initial.code).then((r) => {
      if (!live) return;
      setExchange(r);
      // Step 1 done: straight on to Microsoft's admin consent, no click.
      if (r.outcome === 'redirecting') redirectTo(r.url);
    });
    return () => { live = false; };
  }, [initial]);

  const outcome = initial.outcome === 'code'
    ? (exchange.outcome === 'failed' ? 'error' : exchange.outcome)
    : initial.outcome;
  const message = initial.outcome === 'code' ? exchange.message : initial.message;
  const errorCode = initial.outcome === 'code' ? null : initial.error;
  const refresh = initial.outcome === 'code' && exchange.permissionRefresh === true;
  const live = initial.outcome === 'code' && LIVE_STATUSES.has(exchange.status);
  // A deployed or active customer whose check is still pending (for example the
  // record changed during the check) is not onboarding: no onboarding countdown.
  const livePending = outcome === 'pending' && live;
  const granted = (outcome === 'granted' || outcome === 'pending') && !refresh && !livePending;

  // A granted consent certainly means the consent step started, so the
  // onboarding page opened from here unlocks its fallback continue button.
  useEffect(() => {
    if (granted && initial.customerId) markConsentStarted(initial.customerId);
  }, [granted, initial.customerId]);

  const onboardingPath = initial.customerId
    ? `/onboarding?customerId=${encodeURIComponent(initial.customerId)}`
    : null;
  const overviewPath = initial.customerId
    ? `/customers/${encodeURIComponent(initial.customerId)}/overview`
    : null;

  // Only a granted consent with a known customer returns on its own; an error
  // or an unknown result always waits for the reader.
  const autoReturn = granted && Boolean(onboardingPath);
  const [secondsLeft, setSecondsLeft] = useState(RETURN_COUNTDOWN_SECONDS);
  const [staying, setStaying] = useState(false);

  useEffect(() => {
    if (!autoReturn || staying) return undefined;
    if (secondsLeft <= 0) {
      navigate(onboardingPath, { replace: true });
      return undefined;
    }
    const t = setTimeout(() => setSecondsLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [autoReturn, staying, secondsLeft, navigate, onboardingPath]);

  return (
    <div className="min-h-screen flex flex-col bg-ipc-surface">
      <main className="flex-1 flex justify-center px-4 py-10 sm:py-16">
        <SectionCard as="div" ariaLabel="Microsoft consent result" className="w-full max-w-lg self-start" bodyClassName="space-y-5">
          <Brand tone="dark" />

          {outcome === 'exchanging' && (
            <p className="text-sm text-ipc-muted" role="status">Confirming with Microsoft...</p>
          )}

          {outcome === 'redirecting' && (
            <p className="text-sm text-ipc-muted" role="status">
              Signed in. Continuing to Microsoft to grant the permissions for your organization...{' '}
              <a className="text-ipc-light hover:underline" href={exchange.url}>Continue</a>
            </p>
          )}

          {refresh && (
            <>
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-sm bg-emerald-100" aria-hidden="true">
                  <svg className="h-5 w-5 text-emerald-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <path d="m5 12 5 5 9-11" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <h1 className="text-xl">Permissions updated</h1>
              </div>
              {exchange.granted.length > 0 && (
                <>
                  <p className="text-sm text-ipc-muted">onGuard can now read:</p>
                  <ul className="list-disc pl-5 text-sm text-ipc-ink space-y-1">
                    {exchange.granted.map((name) => (
                      <li key={name}><code className="bg-ipc-surface px-1 rounded text-xs text-ipc-ink">{name}</code></li>
                    ))}
                  </ul>
                </>
              )}
              {exchange.enabledSources.length > 0 && (
                <p className="text-sm text-ipc-muted">Data sources that needed these permissions are enabled on the next collection run.</p>
              )}
              {overviewPath && <Link className="btn-primary inline-block" to={overviewPath}>Back to the customer Overview</Link>}
            </>
          )}

          {livePending && (
            <>
              <h1 className="text-xl">{exchange.settling ? 'Consent recorded' : 'Permissions check pending'}</h1>
              <p className="text-sm text-ipc-muted">
                {exchange.settling
                  ? 'Microsoft recorded the approval and is still applying the new permissions; this can take a few minutes. You do not need to consent again: the portal checks on its own.'
                  : 'Open the customer Overview to see the current permissions.'}
              </p>
              {overviewPath && <Link className="btn-primary inline-block" to={overviewPath}>Back to the customer Overview</Link>}
            </>
          )}

          {granted && (
            <>
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-sm bg-emerald-100" aria-hidden="true">
                  <svg className="h-5 w-5 text-emerald-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <path d="m5 12 5 5 9-11" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <h1 className="text-xl">Consent granted</h1>
              </div>
              <p className="text-sm text-ipc-muted">
                Microsoft recorded the Global Administrator&rsquo;s approval for onGuard.
                onGuard has read-only access to the directory data it collects.
                {outcome === 'pending' && ' Microsoft can take a minute to finish; onboarding continues on its own.'}
              </p>
              {autoReturn ? (
                <div className="rounded-xl border border-ipc-border px-4 py-3 space-y-3">
                  <p className="text-sm text-ipc-ink" aria-hidden="true">
                    {staying
                      ? 'You are staying on this page.'
                      : <>Returning to onboarding in <strong className="tabular-nums">{secondsLeft}s</strong></>}
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <Link className="btn-primary" to={onboardingPath} replace>Return now</Link>
                    {!staying && (
                      <button type="button" className="btn-secondary" onClick={() => setStaying(true)}>
                        Stay on this page
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-ipc-muted">
                    If you are not the person onboarding onGuard, choose Stay on this page and close this tab.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-ipc-muted">
                  Return to the onboarding tab; it continues on its own once Microsoft confirms the consent.
                  If you are not the person onboarding onGuard, you can close this tab.
                </p>
              )}
              {/* Announced once when the countdown starts and once when it is canceled, not every second. */}
              <p className="sr-only" role="status" aria-live="polite">
                {autoReturn && (staying
                  ? 'Automatic return canceled. You are staying on this page.'
                  : `Consent granted. Returning to onboarding in ${RETURN_COUNTDOWN_SECONDS} seconds.`)}
              </p>
            </>
          )}

          {outcome === 'approval' && (
            <>
              <h1 className="text-xl">Consent recorded, approval needed</h1>
              <p className="text-sm text-ipc-muted">
                Microsoft recorded the consent, but this Microsoft Entra tenant does not list the onGuard
                account&rsquo;s email domain as verified. IPC Global approves the tenant before onboarding
                continues. Contact <a className="text-ipc-light hover:underline" href="mailto:support@ipc-global.com">support@ipc-global.com</a>.
              </p>
              {live && overviewPath
                ? <Link className="btn-primary inline-block" to={overviewPath}>Back to the customer Overview</Link>
                : onboardingPath && <Link className="btn-primary inline-block" to={onboardingPath}>Return to onboarding</Link>}
            </>
          )}

          {outcome === 'error' && (
            <>
              <h1 className="text-xl">Consent was not granted</h1>
              <p className="text-sm text-ipc-muted">
                {message}
                {errorCode && <> (Microsoft code <code className="bg-ipc-surface px-1 rounded text-xs text-ipc-ink">{errorCode}</code>)</>}
              </p>
              <p className="text-sm text-ipc-muted">
                A <strong>Global Administrator</strong> must open the consent link, sign in, and click
                {' '}<strong>Accept</strong>. Open the link again from the onboarding page, or
                contact <a className="text-ipc-light hover:underline" href="mailto:support@ipc-global.com">support@ipc-global.com</a>.
              </p>
              {onboardingPath && <Link className="btn-primary inline-block" to={onboardingPath}>Return to onboarding</Link>}
            </>
          )}

          {outcome === 'unknown' && (
            <>
              <h1 className="text-xl">No consent result</h1>
              <p className="text-sm text-ipc-muted">
                This page shows the result of the Microsoft admin-consent step. Open the consent
                link from the onboarding page to grant access.
              </p>
              {onboardingPath && <Link className="btn-primary inline-block" to={onboardingPath}>Return to onboarding</Link>}
            </>
          )}
        </SectionCard>
      </main>
      <BrandFooter />
    </div>
  );
}
